import { useId, useState } from 'react'

import { BorderedRadioButton, RadioGroup } from 'slab'

import { type PlanSelectionPreference } from './type'

type ExpectedPlanUtilizationProps = {
  value?: PlanSelectionPreference['expected_plan_utilization'] | null;
  setValue: (
    value: PlanSelectionPreference['expected_plan_utilization']
  ) => void;
}

export function ExpectedPlanUtilizationV2 ({
  value,
  setValue,
}: ExpectedPlanUtilizationProps) {
  const [selected, setSelected] = useState<
    PlanSelectionPreference['expected_plan_utilization'] | null | undefined
  >(value)

  const choices: {
    value: PlanSelectionPreference['expected_plan_utilization'];
    label: string;
    description: string;
  }[] = [
    {
      value: 'low',
      label: 'A little',
      description:
        'I mostly use preventative care, and have few or no recurring prescriptions. ' +
        'I go to my primary care provider for most needs and almost never hit my deductible.',
    },
    {
      value: 'medium',
      label: 'Somewhat often',
      description:
        'I may be expecting to start a family, have a minor chronic condition, ' +
        "or have a couple of recurring prescriptions. I'm usually okay with going to my primary care provider, " +
        'but sometimes need to go to a specialist.',
    },
    {
      value: 'high',
      label: 'A lot',
      description:
        'I have a significant chronic or pre-existing condition, am often seeing multiple specialists, ' +
        'or have some expensive prescriptions. I hit my deductible most years.',
    },
  ]

  const handleSelect = (
    value: PlanSelectionPreference['expected_plan_utilization']
  ) => {
    setSelected(value)
    setValue(value)
  }

  const headingId = useId()

  return (
    <div>
      <h3 id={headingId} className='heading-sm text-gray-800 mb-6'>
        How often do you expect to use your insurance next year?
      </h3>

      <RadioGroup aria-labelledby={headingId} name='expected_plan_utilization' gap={3}>
        {choices.map((choice) => (
          <BorderedRadioButton
            key={choice.value}
            checked={selected === choice.value}
            description={choice.description}
            label={choice.label}
            onChange={() => handleSelect(choice.value)}
          />
        ))}
      </RadioGroup>
    </div>
  )
}
